/**
 * Lazy-loader for the Razorpay Checkout SDK.
 *
 * Previously the script was loaded eagerly from public/index.html on every
 * route. That added ~1MB of payment JS to the homepage, upload page, style
 * selection page, etc. — pages that have no payment interaction.
 *
 * Now we inject the script the first time a customer actually reaches a
 * route that opens the Razorpay modal (CheckoutPage, PhotoBooksPage).
 * Subsequent calls return the cached promise so we only inject once per
 * SPA session.
 */
const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let rzpPromise = null;

export const loadRazorpay = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("Razorpay can only load in the browser"));
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (rzpPromise) return rzpPromise;

  rzpPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${RZP_SRC}"]`);
    const handleLoad = () => {
      if (window.Razorpay) resolve(window.Razorpay);
      else reject(new Error("Razorpay SDK failed to initialize"));
    };
    if (existing) {
      // Another caller already injected the tag but it hasn't finished
      // loading yet — wait for it.
      existing.addEventListener("load", handleLoad, { once: true });
      existing.addEventListener("error", () => reject(new Error("Razorpay SDK failed to load")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SRC;
    s.async = true;
    s.onload = handleLoad;
    s.onerror = () => {
      // Reset so a later retry can re-inject.
      rzpPromise = null;
      reject(new Error("Razorpay SDK failed to load"));
    };
    document.head.appendChild(s);
  });
  return rzpPromise;
};
